import React from "react";
import { KTMScreen, ActionEvent, RenderEvent, renderScreen } from "ktm-ui";
import { KTMApi } from "api/KTMApi";
import {
  Layout,
  Dialog,
  Button,
  Column,
  Panel,
  Display,
  Line,
  Card,
} from "ktm-ui";
import CustomToolTip from "components/CustomToolTip";
import "../../styles/Employee.css";
import CustomIntegerInput from "components/CustomIntegerInput";
import { MAX_ACCESS_AFTER_TERMINATION_DAYS, MIN_ACCESS_AFTER_TERMINATION_DAYS, clampAccessAfterTerminationDays } from "components/access_after_termination";

interface IdsEmployee {
  Id: string;
  Name: string;
  FirstName: string;
  LastName: string;
  TerminationDate: string;
  AccessAfterTerminationDays: number;
  BenefitEndDate: number;
  TerminationNote: string;
}

export default KTMScreen(
  { name: "planadmin.employee.terminate" },
  class extends React.Component<any, any> {
    data: {
      dsEmployee: IdsEmployee;
    };

    constructor(props: any) {
      super(props);
      this.state = {
        accessDays: clampAccessAfterTerminationDays(props.access_days),
      };
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsEmployee";
      await KTMApi.start(screen, {
        "@employee_id": this.props.employee_id,
      });
      screen.title = "Edit Employee Status";
    }

    render() {
      return renderScreen(this);
    }

    handleInputChange = (value: number) => {
      this.setState((prevState) => ({
        ...prevState,
        accessDays: value,
      }));
    };

    main({ field, draw }: RenderEvent) {
      return (
        <Layout dialogSize="medium">
          {draw(this.header)}
          {draw(this.main_tab)}
          {draw(this.footer)}
        </Layout>
      );
    }

    header({ draw }: RenderEvent) {
      return <Dialog.Header label="Edit Employee Status" />;
    }

    footer({ field, draw, action }: RenderEvent) {
      const today = new Date().setHours(0, 0, 0, 0);
      const benefitEndDate = new Date(this.data.dsEmployee.BenefitEndDate).setHours(0, 0, 0, 0);

      const isInvalidBackdated = this.props.hasAccrual && benefitEndDate < today;

      const disabled =
        this.data.dsEmployee.BenefitEndDate === null ||
        this.data.dsEmployee.BenefitEndDate === undefined ||
        isInvalidBackdated;

      const employeeSubmitButton = (
        <Button
          action={action(this.submit)}
          disable={disabled}
          label="Save"
          size="large"
          style="primary"
        />
      );

      return <Dialog.Footer>{employeeSubmitButton}</Dialog.Footer>;
    }

    main_tab({ draw }: RenderEvent) {
      return (
        <Panel>
          <Column>{draw(this.details)}</Column>
        </Panel>
      );
    }

    details({ field, draw }: RenderEvent) {
      const hasAccrual = this.props.hasAccrual;

      return (
        <Card>
          {hasAccrual && (
            <Line>
              <div>
                The Benefit End Date cannot be backdated previous to today’s date for employees with Scheduled Credits.
              </div>
            </Line>
          )}
          <Line>
            <Display
              field={field("BenefitEndDate")}
              toolTip={
                <CustomToolTip
                  header="Benefit End Date"
                  bodyContent="The last day you want the employee to have coverage. Only expenses incurred prior to this date will be processed."
                />
              }
            />
            <CustomIntegerInput
              label="Access After Termination (days)"
              value={this.state.accessDays}
              min={MIN_ACCESS_AFTER_TERMINATION_DAYS}
              max={MAX_ACCESS_AFTER_TERMINATION_DAYS}
              onChange={this.handleInputChange}
              toolTip={
                <CustomToolTip
                  header="Access After Termination (days)"
                  bodyContent="How long the employee can access their account to log in, make claims, and see their claim history."
                />
              }
            />
          </Line>
          <Line>
            <Display field={field("TerminationNote")} />
          </Line>
        </Card>
      );
    }

    async submit({ screen }: ActionEvent) {
      const payload = {
        employeeId: this.props.employee_id,
        terminationDate: this.data.dsEmployee.TerminationDate,
        benefitEndDate: this.data.dsEmployee.BenefitEndDate,
        accessAfterTermination: this.state.accessDays,
        terminationNote: this.data.dsEmployee.TerminationNote,
      };

      try {
        await fetch("/api/Terminate", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        await fetch("/api/PlanAdmin/updateterminationlogs", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...payload, Action: "Deactivate" }),
        });

        screen.close({ continue: true });
      } catch (error) {
        console.error("Error submitting termination request:", error);
      }
    }
  }
);
